// app/javascript/components/SignaturePad.jsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { t } from '../utils/i18n';

const SignaturePad = ({
  value = '',
  onChange,
  disabled = false,
  title = ''
}) => {
  const [hasStrokes, setHasStrokes] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const fileInputRef = useRef(null);

  // Normalize data URL
  const normalizedValue = React.useMemo(() => {
    if (!value || typeof value !== 'string') return '';
    const cleaned = value.trim();
    if (cleaned.startsWith('data:image/')) return cleaned;
    if (cleaned.startsWith('iVBOR') || cleaned.startsWith('/9j/') || cleaned.startsWith('Qk') || cleaned.startsWith('R0lGOD')) {
      return `data:image/png;base64,${cleaned}`;
    }
    return cleaned;
  }, [value]);

  // Set up canvas sizing and context
  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const width = rect.width || canvas.parentElement?.clientWidth || 280;
    const height = 180;

    canvas.width = width * dpr;
    canvas.height = height * dpr;

    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = '#003366';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  useEffect(() => {
    if (!normalizedValue) {
      const timer = setTimeout(setupCanvas, 60);
      return () => clearTimeout(timer);
    }
  }, [normalizedValue, setupCanvas]);

  // Canvas drawing handlers
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const handlePointerDown = (e) => {
    if (disabled || normalizedValue) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    try {
      if (canvas.setPointerCapture) canvas.setPointerCapture(e.pointerId);
    } catch (_) {}

    isDrawingRef.current = true;
    setHasStrokes(true);
    setErrorMessage('');

    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const handlePointerMove = (e) => {
    if (!isDrawingRef.current || disabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const handlePointerUp = (e) => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    const canvas = canvasRef.current;
    if (canvas) {
      try {
        if (canvas.releasePointerCapture) canvas.releasePointerCapture(e.pointerId);
      } catch (_) {}

      const dataUrl = canvas.toDataURL('image/png');
      if (onChange) onChange(dataUrl);
    }
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setupCanvas();
    }
    setHasStrokes(false);
    setErrorMessage('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (onChange) onChange('');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (PNG or JPG).');
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = ev.target?.result;
      if (result) {
        setErrorMessage('');
        if (onChange) onChange(result);
      }
    };
    reader.readAsDataURL(file);
  };

  // Topaz Pad Capture Handler
  const handleTopazCapture = async () => {
    if (disabled || isCapturing) return;

    setIsCapturing(true);
    setErrorMessage('');

    try {
      let captureSuccess = false;

      // 1. Electron Bridge
      if (window.api?.callSignatureService) {
        try {
          const result = await window.api.callSignatureService('/capture', { action: 'capture' });
          if (result?.success && result?.data?.imageBase64) {
            const rawBase64 = result.data.imageBase64;
            const dataUrl = rawBase64.startsWith('data:') ? rawBase64 : `data:image/png;base64,${rawBase64}`;
            if (onChange) onChange(dataUrl);
            captureSuccess = true;
          } else if (result?.data?.error === 'cancelled' || result?.error === 'cancelled') {
            captureSuccess = true;
          }
        } catch (_) {}
      }

      // 2. Direct HTTP to port 4567
      if (!captureSuccess) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 60000); // 60s for signing
          const res = await fetch('http://127.0.0.1:4567/capture', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'capture' }),
            signal: controller.signal
          });
          clearTimeout(timeoutId);

          if (res.ok) {
            const data = await res.json();
            const rawBase64 = data.imageBase64 || data.data?.imageBase64;
            if (rawBase64) {
              const dataUrl = rawBase64.startsWith('data:') ? rawBase64 : `data:image/png;base64,${rawBase64}`;
              if (onChange) onChange(dataUrl);
              captureSuccess = true;
            } else if (data.error === 'cancelled') {
              captureSuccess = true;
            }
          }
        } catch (_) {
          // Fall through to Rails proxy
        }
      }

      // 3. Rails backend proxy (auto-starts service if stopped)
      if (!captureSuccess) {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
        const headers = {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const proxyRes = await fetch('/api/v1/signature_service/capture', {
          method: 'POST',
          headers,
          body: JSON.stringify({ action: 'capture' })
        });

        if (proxyRes.ok) {
          const proxyData = await proxyRes.json();
          const rawBase64 = proxyData.imageBase64 || proxyData.data?.imageBase64;
          if (rawBase64) {
            const dataUrl = rawBase64.startsWith('data:') ? rawBase64 : `data:image/png;base64,${rawBase64}`;
            if (onChange) onChange(dataUrl);
            captureSuccess = true;
          } else if (proxyData.data?.error === 'cancelled' || proxyData.error === 'cancelled') {
            captureSuccess = true;
          }
        } else {
          const errPayload = await proxyRes.json().catch(() => null);
          throw new Error(errPayload?.error || errPayload?.message || 'Signature capture failed');
        }
      }
    } catch (err) {
      console.error('Failed to capture signature:', err);
      let msg = err.message || '';
      if (msg.includes('Connection refused') || msg.includes('Could not reach') || msg.includes('fetch') || msg.includes('Failed to open TCP')) {
        msg = 'Topaz Signature Service is not running on this PC. Please start "Start-Signature-Service.bat" and plug in the Topaz USB pad, or draw signature directly above.';
      } else if (!msg) {
        msg = 'Device not connected. Please connect Topaz signature pad and try again, or draw signature directly above.';
      }
      setErrorMessage(msg);
    } finally {
      setIsCapturing(false);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb_10">
        <h2 className="txt20 fwMedium colorBlack m-0" data-i18n="biometricSignature">
          {title || t('biometricSignature') || 'Biometric Signature'}
        </h2>
        <div>
          {normalizedValue || hasStrokes ? (
            <a
              href="javascript:void(0);"
              onClick={handleClear}
              className="txt14 colorRed"
              style={{ textDecoration: 'none', fontWeight: 500 }}
              data-i18n="clear"
            >
              {t('clear') || 'Clear'}
            </a>
          ) : (
            <a
              href="javascript:void(0);"
              onClick={() => fileInputRef.current?.click()}
              className="txt14 colorGray"
              style={{ textDecoration: 'none' }}
            >
              📁 {t('upload') || 'Upload'}
            </a>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
        </div>
      </div>

      <div className="posRel signatureWrap">
        <div className="signature-panel">
          {normalizedValue ? (
            <img src={normalizedValue} alt="Biometric Signature" />
          ) : (
            <canvas
              ref={canvasRef}
              id="signature"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
              onPointerCancel={handlePointerUp}
              style={{ touchAction: 'none', cursor: disabled ? 'not-allowed' : 'crosshair' }}
            />
          )}
        </div>
      </div>

      <button
        type="button"
        className="btn inv fullWidth mt_16"
        id="capture_signature"
        onClick={handleTopazCapture}
        disabled={disabled || isCapturing}
        data-i18n="captureSignature"
      >
        {isCapturing ? (
          <span>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Capturing... Sign on Topaz Pad
          </span>
        ) : (
          t('captureSignature') || 'Capture Signature'
        )}
      </button>

      {errorMessage && (
        <p className="txt12 colorRed text-center mt_8 mb_0">
          ⚠️ {errorMessage}
        </p>
      )}
    </>
  );
};

export default SignaturePad;
